import type { SelectMultiChoicelistOption } from 'components/shared/select-multi-choicelist/SelectMultiChoicelist';
import { PackageProviderName } from 'data/ppoints/constants';
import { parseShopifyGidId } from 'hooks/shopify';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { CommonMapper } from 'utils/mapper/common.mapper';
import swrConfig from 'utils/swrConfig';
import { ShippingRateDefinition } from '../../../../../pb/shopify/v1/shipping_rates_pb';
import { getAllMarketShippingRates, MARKET_SHIPPING_RATES_PATH } from '../api/marketShippingRatesApi';
import { MappingShippingRate } from '../models/shipping-rate-mapping';
import { toMappingShippingRate } from '../utils/shipping-rate-details';
import { MappingCountryMarkets } from './useMappingCountryMarkets';

const SECONDS_PER_DAY = 86400;

/** `min–max`, or just the one value when both ends are the same. */
const formatRange = (min: string, max: string): string => (min === max ? min : `${min}–${max}`);

/**
 * `GLS csomagpont (5.00 USD–15.00 USD · 1–10 business days)`: the definition's name, then the
 * cheapest–dearest price and the shortest–longest transit time across all of its rates. A part is
 * left out when no rate carries it; with neither, it's just the name.
 */
const formatDefinitionLabel = (definition: ShippingRateDefinition, t: TFunction): string => {
    const rates = definition.rates ?? [];

    const prices = rates.map((rate) => rate.price).filter((price): price is number => price !== undefined);
    // Transit times are in seconds; an unset one reads as 0 and is skipped.
    const transitDays = rates
        .flatMap((rate) => [rate.minimumTransit, rate.maximumTransit])
        .map((seconds) => CommonMapper.mapBigintIntoNumber(seconds))
        .filter(Boolean)
        .map((seconds) => Math.round(seconds / SECONDS_PER_DAY));

    const formatPrice = (price: number) => [price.toFixed(2), definition.currency].filter(Boolean).join(' ');

    const priceRange = prices.length
        ? formatRange(formatPrice(Math.min(...prices)), formatPrice(Math.max(...prices)))
        : undefined;

    const transitRange = transitDays.length
        ? t('SHIPPING_PROVIDERS.SHIPPING_RATE_SELECTOR.TRANSIT_DAYS', {
              count: Math.max(...transitDays),
              range: formatRange(String(Math.min(...transitDays)), String(Math.max(...transitDays))),
          })
        : undefined;

    const details = [priceRange, transitRange].filter(Boolean).join(' · ');

    return details ? `${definition.name} (${details})` : definition.name;
};

export type RateOptionsByCountry = Record<string, SelectMultiChoicelistOption<number>[]>;

export interface UseShippingRateOptionsByCountryReturn {
    optionsByCountry: RateOptionsByCountry;
    /**
     * Every live rate by its numeric id, in the shape the rate mapping PUT takes — the option labels
     * carry the price and transit time, so they can't double as the saved rate name.
     */
    ratesById: Map<number, MappingShippingRate>;
    isLoading: boolean;
    error?: Error;
    /** Revalidates the rates — call after the merchant adds some in the Shopify market editor. */
    mutate(): void;
}

/**
 * The rates each country can be mapped to, keyed by ISO country code: every shipping rate definition
 * of the markets covering that country, from `/api/v1/markets`.
 *
 * The markets per country come from `useMappingCountryMarkets` (`countries`); the endpoint's entries
 * are matched to them by `marketId`. A country covered by several markets is offered all of their
 * definitions, each once.
 */
export const useShippingRateOptionsByCountry = (
    provider: PackageProviderName,
    countries: MappingCountryMarkets[]
): UseShippingRateOptionsByCountryReturn => {
    const { t } = useTranslation();
    const hasMarkets = countries.some((country) => country.markets.length > 0);

    const { data, isLoading, error, mutate } = useSWR(
        hasMarkets ? [MARKET_SHIPPING_RATES_PATH, provider] : null,
        () => getAllMarketShippingRates(provider),
        // Same reason as the markets query in `useMappingCountryMarkets`: without this every selector
        // falls back to its "no rates" empty state for the duration of a refetch.
        { ...swrConfig, keepPreviousData: true }
    );

    const definitionsByMarketId = useMemo(() => {
        const byMarketId = new Map<string, ShippingRateDefinition[]>();

        data?.forEach(({ marketId, definitions }) => {
            byMarketId.set(marketId, [...(byMarketId.get(marketId) ?? []), ...definitions]);
        });

        return byMarketId;
    }, [data]);

    const optionsByCountry = useMemo(
        () =>
            countries.reduce<RateOptionsByCountry>((acc, country) => {
                const seen = new Set<number>();

                acc[country.countryCode] = country.markets
                    .flatMap((market) => definitionsByMarketId.get(market.id) ?? [])
                    .map((definition) => ({
                        value: parseShopifyGidId(definition.id),
                        label: formatDefinitionLabel(definition, t),
                    }))
                    .filter((option) => {
                        if (option.value === undefined || seen.has(option.value)) return false;

                        seen.add(option.value);
                        return true;
                    })
                    .map((option) => ({ value: option.value, label: option.label || `#${option.value}` }));

                return acc;
            }, {}),
        [countries, definitionsByMarketId, t]
    );

    const ratesById = useMemo(() => {
        const byId = new Map<number, MappingShippingRate>();

        definitionsByMarketId.forEach((definitions) =>
            definitions.forEach((definition) => {
                const rate = toMappingShippingRate(definition);

                if (rate) byId.set(rate.id, rate);
            })
        );

        return byId;
    }, [definitionsByMarketId]);

    return { optionsByCountry, ratesById, isLoading, error, mutate };
};
